import { create } from 'zustand';
import { toast } from '../../store/alerts.js';
import { isDefined } from '@fabric-app/utils/guards';
import { generatePathResourceDetail } from '../../app/routing/routes.config.js';
import plur from 'plur';
import { isResourceDetail } from '../utils/resourceDetailGuards.js';
import { pick } from '@fabric-app/utils/object/manipulation';
import { getCompatUI } from '@fabric-app/compat/capabilities/ui';
import { useCallback, useEffect } from 'react';
import { shallow as shallow$1 } from '../../../node_modules/zustand/esm/shallow.js';
import { useSetQueryResourceDetailData } from '../queries/useQueryResourceDetail.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { getRouteParamAsString } from '@fabric-app/utils/url/routing';
import { useRouter } from '@fabric-app/compat';
import { delayedTrackZustand } from '@fabric-app/compat/capabilities/openreplay';

const useExpandedResourceStore = create()(delayedTrackZustand((set, get) => {
  return {
    expandedResourceId: null,
    expandedResourceDisableSwicher: false,
    setExpandedResourceId: (id, options) => {
      set((s) => {
        if (s.expandedResourceId === id) {
          return s;
        }
        const nextState = {
          expandedResourceId: id,
          expandedResourceSidebarTab: options?.tab ?? options?.preserveCurrentTab ? s.expandedResourceSidebarTab : "assistant",
          expandedResourceSidebarInitTab: options?.tab ?? void 0,
          expandedResourceSidebarOpen: isDefined(options?.tab) ? true : s.expandedResourceSidebarOpen,
          visitedResourceHistoryIds: options?.visitedResourceHistoryIds || (s.expandedResourceId && id && options?.storeCurrentAsPrevious ? s.expandedResourceId !== id ? [
            ...s.visitedResourceHistoryIds,
            s.expandedResourceId
          ] : s.visitedResourceHistoryIds : []),
          expandedResourceDisableSwicher: isDefined(options?.disableSwitcher) ? options.disableSwitcher : false
        };
        return nextState;
      });
    },
    expandedResourceSidebarOpen: true,
    expandedResourceSidebarInitTab: "assistant",
    expandedResourceSidebarTab: "assistant",
    visitedResourceHistoryIds: [],
    setExpandedResourceSidebarOpen: (open) => set({
      expandedResourceSidebarOpen: open
    }),
    setExpandedResourceDisableSwicher: (disableSwitcher) => set({
      expandedResourceDisableSwicher: disableSwitcher
    }),
    setExpandedResourceSidebarTab: (tab) => set({
      expandedResourceSidebarTab: tab,
      expandedResourceSidebarInitTab: void 0
    }),
    expandedResourcePendingUploads: /* @__PURE__ */ new Set(),
    isExpandedResourceLocked: (options) => {
      const uploadProgressCount = get().expandedResourcePendingUploads.size;
      if (uploadProgressCount > 0 && !options?.disableToast) {
        toast({
          content: `${plur("File", uploadProgressCount)} still uploading... hold on a sec!`,
          id: "expanded-resource-locked"
        });
        return true;
      }
      return false;
    }
  };
}));
const useShowExpandedResource = () => {
  const onExpandedResource = getCompatUI().useOnExpandedResource();
  const setQueryResourceDetailData = useSetQueryResourceDetailData();
  const { track } = useAnalytics();
  return useCallback((arg, options) => {
    if (useExpandedResourceStore.getState().isExpandedResourceLocked()) return;
    const resource = !!arg && typeof arg !== "string" && arg !== null && isResourceDetail(arg) ? arg : void 0;
    const resourceId = typeof arg === "string" ? arg : resource?.id;
    if (resourceId) {
      track(AnalyticsEvents.ViewFdoc, {
        resourceId,
        action: "expanded_fdoc",
        type: resource?.kind
      });
    }
    const result = onExpandedResource(arg, options);
    if (result) return;
    if (resource) {
      setQueryResourceDetailData(resource);
    }
    if (options?.openInNewTab && resourceId) {
      window.open(generatePathResourceDetail(resourceId));
      return;
    }
    useExpandedResourceStore.getState().setExpandedResourceId(resourceId ?? null, options);
  }, [
    setQueryResourceDetailData,
    onExpandedResource,
    track
  ]);
};
const useExpandedResource = () => {
  const expandedResourceId = useExpandedResourceStore((s) => s.expandedResourceId, shallow$1);
  return expandedResourceId;
};
const useSyncExpandedResourceIdWithRouter = () => {
  const router = useRouter();
  const { query } = router;
  const showExpandedResource = useShowExpandedResource();
  const [expandedResourceIdFromRouter] = getRouteParamAsString(query, [
    "expandedFdocId"
  ]);
  useEffect(() => {
    const expandedResourceId = useExpandedResourceStore.getState().expandedResourceId;
    if (expandedResourceIdFromRouter === expandedResourceId) return;
    showExpandedResource(expandedResourceIdFromRouter ?? null);
  }, [
    expandedResourceIdFromRouter,
    showExpandedResource
  ]);
};
const useExpandedResourceState = () => {
  const { expandedResourceSidebarOpen, expandedResourceSidebarInitTab, expandedResourceSidebarTab, setExpandedResourceSidebarOpen, setExpandedResourceSidebarTab } = useExpandedResourceStore((s) => pick(s, [
    "expandedResourceSidebarOpen",
    "expandedResourceSidebarInitTab",
    "expandedResourceSidebarTab",
    "setExpandedResourceSidebarOpen",
    "setExpandedResourceSidebarTab"
  ]), shallow$1);
  return {
    expandedResourceSidebarOpen,
    expandedResourceSidebarInitTab,
    expandedResourceSidebarTab,
    setExpandedResourceSidebarOpen,
    setExpandedResourceSidebarTab
  };
};
const isExpandedResourceLocked = (options) => useExpandedResourceStore.getState().isExpandedResourceLocked(options);
const useShowPreviousExpandedResource = () => {
  const { visitedResourceHistoryIds, setExpandedResourceId, expandedResourceDisableSwicher } = useExpandedResourceStore((s) => pick(s, [
    "visitedResourceHistoryIds",
    "setExpandedResourceId",
    "expandedResourceDisableSwicher"
  ]), shallow$1);
  return visitedResourceHistoryIds.length > 0 ? () => {
    const nextArray = [
      ...visitedResourceHistoryIds
    ];
    const lastItem = nextArray.pop();
    setExpandedResourceId(lastItem ?? null, {
      visitedResourceHistoryIds: nextArray,
      disableSwitcher: nextArray.length > 0 ? expandedResourceDisableSwicher : false
    });
  } : void 0;
};

export { isExpandedResourceLocked, useExpandedResource, useExpandedResourceState, useExpandedResourceStore, useShowExpandedResource, useShowPreviousExpandedResource, useSyncExpandedResourceIdWithRouter };
//# sourceMappingURL=useExpandedResourceStore.js.map
